'use client';

import React, {
  type MutableRefObject,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import {
  PopoverWrapper,
  PopoverStickyHeader,
  PopoverFooterShadow,
  PopoverBody,
} from '@/app/common/components/Popover/styled.tsx';
import OverlayContext from '@/app/common/context/OverlayContext.ts';
import useScrollShadows from '@/app/common/hooks/useScrollShadows.ts';

// keep in sync with pill CSS transition duration
const TRANSITION_TRACK_MS = 400;

export type PopoverPlacement = 'top' | 'bottom';
export type PopoverAlign = 'left' | 'center' | 'right' | 'start';
export type PopoverTrigger = 'click' | 'hover' | 'both';

export type PopoverProps = {
  id: string;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  placement?: PopoverPlacement;
  align?: PopoverAlign;
  children: ReactNode;
  'aria-label'?: string;
  padding?: string;
  borderRadius?: string;
  width?: string;
  maxWidth?: string;
  maxHeight?: string;
  boxShadow?: string;
  trigger?: PopoverTrigger;
  className?: string;
  closeOnScroll?: boolean;
  title?: ReactNode;
  footer?: ReactNode;
  usePortal?: boolean;
};

const VERTICAL_GAP = 4;
const HORIZONTAL_MARGIN = 8;

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const getVisibleFocusableElements = (container: HTMLElement): HTMLElement[] =>
  Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) =>
      !el.hasAttribute('disabled') && el.getAttribute('aria-hidden') !== 'true' && !el.inert && el.offsetParent !== null
  );

const pickVerticalPlacement = (
  preferred: PopoverPlacement,
  anchorRect: DOMRect,
  popoverHeight: number
): PopoverPlacement => {
  const spaceBelow = window.innerHeight - anchorRect.bottom;
  const spaceAbove = anchorRect.top;
  if (preferred === 'bottom')
    return spaceBelow >= popoverHeight + VERTICAL_GAP || spaceBelow >= spaceAbove ? 'bottom' : 'top';
  return spaceAbove >= popoverHeight + VERTICAL_GAP || spaceAbove >= spaceBelow ? 'top' : 'bottom';
};

const resolveLeft = (align: PopoverAlign, anchorRect: DOMRect, popoverWidth: number): number => {
  let left: number;
  if (align === 'right') {
    left = anchorRect.right - popoverWidth;
  } else if (align === 'left') {
    left = anchorRect.left;
  } else if (align === 'start') {
    const leftAligned = anchorRect.left;
    left =
      leftAligned + popoverWidth + HORIZONTAL_MARGIN > window.innerWidth
        ? anchorRect.right - popoverWidth
        : leftAligned;
  } else {
    left = anchorRect.left + anchorRect.width / 2 - popoverWidth / 2;
  }
  return Math.max(HORIZONTAL_MARGIN, Math.min(left, window.innerWidth - popoverWidth - HORIZONTAL_MARGIN));
};

const resolveTop = (placement: PopoverPlacement, anchorRect: DOMRect, popoverHeight: number): number => {
  const effectivePlacement = pickVerticalPlacement(placement, anchorRect, popoverHeight);
  return effectivePlacement === 'bottom'
    ? anchorRect.bottom + VERTICAL_GAP
    : anchorRect.top - popoverHeight - VERTICAL_GAP;
};

const computePortalCoords = (
  anchorEl: HTMLElement,
  popoverEl: HTMLElement,
  placement: PopoverPlacement,
  align: PopoverAlign
): { top: number; left: number } => {
  const anchorRect = anchorEl.getBoundingClientRect();
  return {
    top: resolveTop(placement, anchorRect, popoverEl.offsetHeight),
    left: resolveLeft(align, anchorRect, popoverEl.offsetWidth),
  };
};

const computeInlineCoords = (
  anchorEl: HTMLElement,
  popoverEl: HTMLElement,
  placement: PopoverPlacement,
  align: PopoverAlign
): { top: number; left: number } => {
  const anchorRect = anchorEl.getBoundingClientRect();
  const parentRect = (popoverEl.offsetParent as HTMLElement | null)?.getBoundingClientRect() ?? { top: 0, left: 0 };
  return {
    top: resolveTop(placement, anchorRect, popoverEl.offsetHeight) - parentRect.top,
    left: resolveInlineLeft(align, anchorRect, popoverEl.offsetWidth, parentRect.left),
  };
};

const resolveInlineLeft = (
  align: PopoverAlign,
  anchorRect: DOMRect,
  popoverWidth: number,
  parentLeft: number
): number => {
  if (align === 'right') return anchorRect.right - popoverWidth - parentLeft;
  if (align === 'left') return anchorRect.left - parentLeft;
  return anchorRect.left + anchorRect.width / 2 - popoverWidth / 2 - parentLeft;
};

const bindScrollAndResize = (
  popover: HTMLElement | null,
  onScroll: (e: Event) => void,
  onResize: () => void
): (() => void) => {
  window.addEventListener('scroll', onScroll, true);
  window.addEventListener('resize', onResize);
  return () => {
    window.removeEventListener('scroll', onScroll, true);
    window.removeEventListener('resize', onResize);
  };
};

const bindKeyboard = (onClose: () => void): (() => void) => {
  const handler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  };
  document.addEventListener('keydown', handler);
  return () => document.removeEventListener('keydown', handler);
};

const bindOutsideClick = (anchor: HTMLElement, popover: HTMLElement | null, onClose: () => void): (() => void) => {
  const handler = (e: MouseEvent) => {
    if (popover?.contains(e.target as Node) || anchor.contains(e.target as Node)) return;
    onClose();
  };
  document.addEventListener('mousedown', handler);
  return () => document.removeEventListener('mousedown', handler);
};

const bindListeners = (
  anchor: HTMLElement,
  popover: HTMLElement | null,
  trigger: PopoverTrigger,
  onClose: () => void,
  computePosition: () => void,
  closeOnScroll: boolean
): (() => void) => {
  let rafId: number | null = null;

  const trackPositionThroughTransition = () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    const start = performance.now();
    const tick = () => {
      computePosition();
      if (performance.now() - start < TRANSITION_TRACK_MS) {
        rafId = requestAnimationFrame(tick);
      } else {
        rafId = null;
      }
    };
    rafId = requestAnimationFrame(tick);
  };

  const onScroll = (e: Event) => {
    if (popover?.contains(e.target as Node)) return;
    if (closeOnScroll) onClose();
    else trackPositionThroughTransition();
  };

  const cleanups = [
    bindScrollAndResize(popover, onScroll, computePosition),
    bindKeyboard(onClose),
    ...(trigger !== 'hover' ? [bindOutsideClick(anchor, popover, onClose)] : []),
  ];

  return () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    cleanups.forEach((fn) => fn());
  };
};

const elevateParentCard = (anchorEl: HTMLElement): (() => void) | undefined => {
  const card = anchorEl.closest<HTMLElement>('[data-testid="card-container"]');
  if (!card) return undefined;
  card.style.zIndex = '2';
  return () => {
    card.style.zIndex = '';
  };
};

const manageFocus = (
  container: HTMLElement,
  restoreFocusRef: MutableRefObject<HTMLElement | null>,
  onClose: () => void
): (() => void) => {
  const focusable = getVisibleFocusableElements(container);
  const rafId = requestAnimationFrame(() => (focusable[0] ?? container).focus());

  const onFocusOut = (e: FocusEvent) => {
    const next = e.relatedTarget as Node | null;
    if (!next || !container.contains(next)) onClose();
  };

  container.addEventListener('focusout', onFocusOut);

  return () => {
    cancelAnimationFrame(rafId);
    container.removeEventListener('focusout', onFocusOut);
    restoreFocusRef.current?.focus();
  };
};

const Popover = ({
  id,
  anchorEl,
  onClose,
  placement = 'bottom',
  align = 'center',
  padding = '24px',
  borderRadius = '16px',
  width,
  maxWidth,
  maxHeight,
  boxShadow,
  trigger = 'click',
  children,
  className,
  closeOnScroll: closeOnScrollProp,
  'aria-label': ariaLabel,
  title,
  footer,
  usePortal = true,
}: PopoverProps) => {
  const closeOnScroll = closeOnScrollProp ?? trigger !== 'click';
  const popoverRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const keyboardTriggeredRef = useRef(false);
  const onCloseRef = useRef(onClose);
  const [isPositioned, setIsPositioned] = useState(false);
  const { probeRef, headerShadow, footerShadow } = useScrollShadows();

  useEffect(() => {
    onCloseRef.current = onClose;
  });

  const inline = !usePortal;

  const computePosition = useCallback(() => {
    if (!anchorEl || !popoverRef.current) return;
    const coords = inline
      ? computeInlineCoords(anchorEl, popoverRef.current, placement, align)
      : computePortalCoords(anchorEl, popoverRef.current, placement, align);
    popoverRef.current.style.top = `${coords.top}px`;
    popoverRef.current.style.left = `${coords.left}px`;
  }, [anchorEl, placement, align, inline]);

  useLayoutEffect(() => {
    if (!anchorEl) {
      setIsPositioned(false);
      return;
    }
    computePosition();
    setIsPositioned(true);
  }, [anchorEl, computePosition]);

  useEffect(() => {
    if (!anchorEl) return;
    if (document.activeElement instanceof HTMLElement) {
      previousFocusRef.current = document.activeElement;
      keyboardTriggeredRef.current = document.activeElement.matches(':focus-visible');
    }
  }, [anchorEl]);

  useEffect(() => {
    if (!isPositioned || !popoverRef.current || trigger === 'hover' || !keyboardTriggeredRef.current) return undefined;
    return manageFocus(popoverRef.current, previousFocusRef, () => onCloseRef.current());
  }, [isPositioned, trigger]);

  useEffect(() => {
    if (!anchorEl) return undefined;
    return bindListeners(
      anchorEl,
      popoverRef.current,
      trigger,
      () => onCloseRef.current(),
      computePosition,
      closeOnScroll
    );
  }, [anchorEl, trigger, computePosition, closeOnScroll]);

  useEffect(() => {
    if (!inline || !isPositioned || !anchorEl) return undefined;
    return elevateParentCard(anchorEl);
  }, [inline, isPositioned, anchorEl]);

  const overlayContextValue = useMemo(() => ({ headerShadow, footerShadow }), [headerShadow, footerShadow]);

  if (!anchorEl || typeof document === 'undefined') return null;

  const popoverElement = (
    <OverlayContext.Provider value={overlayContextValue}>
      <PopoverWrapper
        ref={popoverRef}
        $visible={isPositioned}
        $padding={title ? '0' : padding}
        $borderRadius={borderRadius}
        $width={width}
        $maxWidth={maxWidth ?? width ?? '320px'}
        $maxHeight={maxHeight}
        $boxShadow={boxShadow}
        $inline={inline}
        id={id}
        role="dialog"
        aria-modal={!inline}
        aria-label={ariaLabel}
        aria-hidden={!isPositioned}
        tabIndex={-1}
        className={className}
      >
        <div ref={probeRef} style={{ display: 'none' }} aria-hidden="true" />
        {title && <PopoverStickyHeader handleClose={onClose}>{title}</PopoverStickyHeader>}
        {title ? <PopoverBody $padding={padding}>{children}</PopoverBody> : children}
        {footer ?? <PopoverFooterShadow $showShadow={footerShadow} aria-hidden="true" />}
      </PopoverWrapper>
    </OverlayContext.Provider>
  );

  return inline ? popoverElement : ReactDOM.createPortal(popoverElement, document.body);
};

export default Popover;
